import React, { useState } from 'react';
import axios from 'axios';
import toast from 'react-hot-toast';
import { API, getAuthHeaders } from './shared';

export default function UsersTab({ users, onCreateUser, onEditUser, onDeleteUser, onRefresh }) {
  const [selectedUsers, setSelectedUsers] = useState(new Set());
  const [bulkAction, setBulkAction] = useState(null);
  const [bulkForm, setBulkForm] = useState({ role: 'Member' });

  const toggleUserSelect = (id) => {
    setSelectedUsers(prev => {
      const next = new Set(prev);
      next.has(id) ? next.delete(id) : next.add(id);
      return next;
    });
  };

  const toggleAllUsers = () => {
    if (selectedUsers.size === users.length) {
      setSelectedUsers(new Set());
    } else {
      setSelectedUsers(new Set(users.map(u => u.id)));
    }
  };

  const executeBulkAction = async (action, extraData = {}) => {
    const ids = [...selectedUsers];
    const headers = getAuthHeaders();
    try {
      let endpoint, body;
      if (action === 'delete') {
        if (!window.confirm(`Er du sikker på at du vil slette ${ids.length} brukere permanent?`)) return;
        endpoint = `${API}/ops/system/bulk/delete-users`;
        body = { user_ids: ids };
      } else {
        endpoint = `${API}/ops/system/bulk/update-users`;
        body = { user_ids: ids, action, ...extraData };
      }
      const res = await axios.post(endpoint, body, { headers });
      toast.success(res.data.message);
      setSelectedUsers(new Set());
      setBulkAction(null);
      onRefresh();
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Bulk-operasjon feilet');
    }
  };

  return (
    <div className="space-y-4">
      <div className="flex justify-between items-center">
        <h2 className="text-lg font-semibold">Alle brukere ({users.length})</h2>
        <button onClick={onCreateUser} className="px-4 py-2 bg-red-600 text-white rounded-lg text-sm font-medium hover:bg-red-700">
          + Opprett bruker
        </button>
      </div>

      {selectedUsers.size > 0 && (
        <div className="bg-cyan-900/30 border border-cyan-700/40 rounded-xl p-3 flex items-center gap-3 flex-wrap" data-testid="user-bulk-bar">
          <span className="text-sm text-cyan-300 font-medium">{selectedUsers.size} valgt</span>
          <div className="h-5 w-px bg-cyan-700/50" />
          <button onClick={() => executeBulkAction('delete')} className="px-3 py-1.5 bg-red-600 text-white text-xs rounded-lg hover:bg-red-700" data-testid="user-bulk-delete">Slett</button>
          <button onClick={() => setBulkAction('user-role')} className="px-3 py-1.5 bg-purple-600 text-white text-xs rounded-lg hover:bg-purple-700" data-testid="user-bulk-role">Endre rolle</button>
          <button onClick={() => executeBulkAction('deactivate')} className="px-3 py-1.5 bg-amber-600 text-white text-xs rounded-lg hover:bg-amber-700" data-testid="user-bulk-deactivate">Deaktiver</button>
          <button onClick={() => executeBulkAction('activate')} className="px-3 py-1.5 bg-green-600 text-white text-xs rounded-lg hover:bg-green-700" data-testid="user-bulk-activate">Aktiver</button>
          <button onClick={() => setSelectedUsers(new Set())} className="px-3 py-1.5 text-gray-400 text-xs hover:text-white">Avbryt</button>
        </div>
      )}

      {bulkAction === 'user-role' && (
        <div className="bg-gray-800 border border-gray-700 rounded-xl p-4 flex items-center gap-4" data-testid="user-bulk-role-form">
          <span className="text-sm text-gray-300">Endre rolle for {selectedUsers.size} brukere til</span>
          <select value={bulkForm.role} onChange={e => setBulkForm({...bulkForm, role: e.target.value})} className="bg-gray-700 border border-gray-600 rounded px-2 py-1 text-white text-sm" data-testid="user-bulk-role-select">
            <option value="Admin">Admin</option>
            <option value="PowerUser">PowerUser</option>
            <option value="Member">Member</option>
          </select>
          <button onClick={() => executeBulkAction('change_role', { role: bulkForm.role })} className="px-3 py-1.5 bg-cyan-600 text-white text-xs rounded-lg" data-testid="user-bulk-role-submit">Utfør</button>
          <button onClick={() => setBulkAction(null)} className="text-gray-500 text-xs hover:text-white">Avbryt</button>
        </div>
      )}

      <div className="bg-gray-800 rounded-lg divide-y divide-gray-700">
        <div className="p-3 flex items-center gap-3 bg-gray-800/80">
          <input type="checkbox" checked={selectedUsers.size === users.length && selectedUsers.size > 0} onChange={toggleAllUsers} className="w-4 h-4 accent-cyan-500" data-testid="user-select-all" />
          <span className="text-xs text-gray-500">Velg alle</span>
        </div>
        {users.map(user => (
          <div key={user.id} className={`p-4 flex items-center gap-3 ${selectedUsers.has(user.id) ? 'bg-cyan-900/10' : ''}`}>
            <input type="checkbox" checked={selectedUsers.has(user.id)} onChange={() => toggleUserSelect(user.id)} className="w-4 h-4 accent-cyan-500 flex-shrink-0" data-testid={`user-select-${user.id}`} />
            <div className="flex-1">
              <div className="font-medium flex items-center gap-2">
                {user.name}
                {user.is_test_user && <span className="px-2 py-0.5 bg-yellow-600 text-xs rounded">TEST</span>}
                {user.is_active === false && <span className="px-2 py-0.5 bg-gray-600 text-xs rounded">INAKTIV</span>}
              </div>
              <div className="text-sm text-gray-400">{user.email} &bull; {user.organization_name || 'Ukjent org'} &bull; {user.role}</div>
            </div>
            <div className="flex items-center gap-3">
              <button onClick={() => onEditUser(user)} className="text-blue-400 hover:text-blue-300 text-sm">Rediger</button>
              <button onClick={() => onDeleteUser(user)} className="text-red-400 hover:text-red-300 text-sm">Slett</button>
            </div>
          </div>
        ))}
      </div>
    </div>
  );
}
